import React, { useState, useEffect, useRef } from 'react';
import { 
  Store, 
  MapPin, 
  Phone, 
  Clock,
  Truck,
  ShoppingBag,
  Utensils,
  Euro,
  Save,
  ExternalLink,
  Globe,
  Link as LinkIcon,
  Copy,
  Check,
  CreditCard,
  Banknote,
  Calendar,
  QrCode,
  Smartphone,
  Crown,
  Settings,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAutosave } from '../hooks/useAutosave';
import { AutosaveStatus } from '../components/AutosaveStatus';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { restaurantAPI, authAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

// Reusable image upload field with URL input + file upload button
const ImageUploadField = ({ id, label, hint, value, onChange, maxKb = 2048, testId, previewClass }) => {
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxKb * 1024) {
      toast.error(`Afbeelding te groot (max ${Math.round(maxKb / 1024 * 10) / 10} MB)`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {value && (
        <div className="relative inline-block">
          <img src={value} alt={label} className={previewClass || 'h-20 w-20 object-cover rounded-xl'} />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold shadow hover:bg-red-600"
            title="Verwijderen"
            data-testid={`${testId}-remove-btn`}
          >
            ✕
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <Input
          id={id}
          type="url"
          placeholder="https://... of klik Upload"
          value={value?.startsWith('data:') ? '' : (value || '')}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testId}-url-input`}
          className="flex-1"
        />
        <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 hover:bg-gray-100 whitespace-nowrap" data-testid={`${testId}-upload-label`}>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFile}
            data-testid={`${testId}-file-input`}
          />
          Upload
        </label>
      </div>
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
};

export const SettingsPage = () => {
  const { restaurant, updateRestaurant } = useAuth();
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    postal_code: '',
    city: '',
    cuisine_type: '',
    description: '',
    logo_url: '',
    cover_image_url: '',
    receipt_logo_url: '',
    receipt_font_size: 'normal',
    vat_number: '',
    // Services
    delivery_enabled: true,
    pickup_enabled: true,
    dine_in_enabled: true,
    reservations_enabled: true,
    // Delivery settings
    default_delivery_fee: '',
    default_min_order_amount: '',
    estimated_delivery_time: '',
    estimated_pickup_time: '',
    // Payment methods
    cash_enabled: true,
    card_enabled: false,
    online_payment_enabled: true,
    // Language & Currency
    currency: 'EUR',
    default_language: 'nl',
    // Domain
    custom_domain: '',
    custom_domain_verified: false,
    custom_domain_active: false,
    // Opening hours
    opening_hours: {
      monday: { open: '09:00', close: '22:00', closed: false },
      tuesday: { open: '09:00', close: '22:00', closed: false },
      wednesday: { open: '09:00', close: '22:00', closed: false },
      thursday: { open: '09:00', close: '22:00', closed: false },
      friday: { open: '09:00', close: '23:00', closed: false },
      saturday: { open: '10:00', close: '23:00', closed: false },
      sunday: { open: '10:00', close: '22:00', closed: false },
    },
    // Scheduled orders (pre-orders)
    scheduled_orders_enabled: true,
    scheduled_orders_lead_time_minutes: 30,
    scheduled_orders_slot_interval_minutes: 30,
    scheduled_orders_max_days_ahead: 1,
    scheduled_orders_max_per_slot: 5,
    scheduled_orders_prep_warning_minutes: 10,
  });
  const [fetching, setFetching] = useState(true);

  // ---- automatisch opslaan --------------------------------------------
  // Wat zichzelf opslaat: naam, adres, taal, lettergrootte, welke diensten je
  // aanbiedt. Wat een knop houdt: bedragen, openingstijden en betaalmethodes.
  // Daar kost een typfout direct geld of blokkeert hij bestellingen, en dan is
  // stil doorvoeren erger dan een extra klik.
  const bewaard = useRef({});           // laatst bekende opgeslagen waarden
  const [vuil, setVuil] = useState({}); // secties met een eigen knop
  const vuilRef = useRef(vuil);
  vuilRef.current = vuil;
  const [sectieBezig, setSectieBezig] = useState(null);

  const autosave = useAutosave(
    async (velden) => {
      const response = await restaurantAPI.updateProfile(velden);
      updateRestaurant(response.data);
      bewaard.current = { ...bewaard.current, ...velden };
    },
    {
      onOpgegeven: () => toast.error(
        'Je wijziging is niet opgeslagen. Controleer je internetverbinding en '
        + 'klik daarna op "Opnieuw proberen".', { duration: 15000 }),
      ookOpenstaand: () => Object.values(vuilRef.current).some(Boolean),
    });

  // Een veld dat zichzelf opslaat.
  const zet = (velden) => {
    setFormData((vorige) => ({ ...vorige, ...velden }));
    autosave.plan(velden);
  };

  // Numerieke velden staan als tekst in het formulier, zodat je ze tijdens het
  // typen leeg kunt maken. Opgestuurd wordt het getal: een lege string zou de
  // backend afkeuren.
  const zetGetal = (veld, waarde, standaard) => {
    setFormData((vorige) => ({ ...vorige, [veld]: waarde }));
    const n = parseInt(waarde, 10);
    autosave.plan({ [veld]: Number.isFinite(n) ? n : standaard });
  };

  // Een veld in een sectie met een eigen opslaan-knop.
  const zetHandmatig = (sectie, velden) => {
    setFormData((vorige) => ({ ...vorige, ...velden }));
    setVuil((v) => ({ ...v, [sectie]: true }));
  };

  const zetUren = (dag, wijziging) => {
    setFormData((vorige) => ({
      ...vorige,
      opening_hours: {
        ...vorige.opening_hours,
        [dag]: {
          ...(vorige.opening_hours[dag] || { open: '09:00', close: '22:00', closed: false }),
          ...wijziging,
        },
      },
    }));
    setVuil((v) => ({ ...v, uren: true }));
  };

  const DIENST_UIT = {
    delivery_enabled: 'Bezorgen staat nu uit. Klanten kunnen niet meer laten bezorgen.',
    pickup_enabled: 'Afhalen staat nu uit. Klanten kunnen niet meer afhalen.',
    dine_in_enabled: 'Ter plaatse eten staat nu uit op je webshop.',
    reservations_enabled: 'Reserveren staat nu uit op je webshop.',
  };

  // Een schakelaar kun je niet vertypen, dus die mag zichzelf opslaan. Het
  // risico zit elders: niet doorhebben wat je net hebt uitgezet. Daarom bij het
  // uitzetten wel zeggen wat het gevolg is.
  const zetDienst = (veld, aan) => {
    zet({ [veld]: aan });
    if (aan) return;
    const na = { ...formData, [veld]: false };
    if (!na.delivery_enabled && !na.pickup_enabled) {
      toast.warning('Bezorgen en afhalen staan nu allebei uit — klanten kunnen '
                    + 'niets meer bestellen.', { duration: 15000 });
    } else {
      toast.warning(DIENST_UIT[veld], { duration: 8000 });
    }
  };

  const slaSectieOp = async (sectie, velden, label) => {
    setSectieBezig(sectie);
    try {
      const response = await restaurantAPI.updateProfile(velden);
      updateRestaurant(response.data);
      bewaard.current = { ...bewaard.current, ...velden };
      setVuil((v) => ({ ...v, [sectie]: false }));
      toast.success(label + ' opgeslagen');
    } catch (error) {
      toast.error(label + ' kon niet worden opgeslagen. Je wijziging staat er '
                  + 'nog — probeer het opnieuw.', { duration: 12000 });
    } finally {
      setSectieBezig(null);
    }
  };

  // Een verschoven komma gaat twee kanten op: 25 in plaats van 2,50 is te duur,
  // 2 in plaats van 20 kost bij elke bestelling geld. Alleen op verdubbelen
  // controleren zou de helft van de gevallen missen.
  const bevestigBedrag = (zin, oud, nieuw) => {
    const o = parseFloat(oud);
    const n = parseFloat(nieuw);
    if (!isFinite(o) || !isFinite(n) || o <= 0 || n === o) return true;
    const factor = n / o;
    if (factor <= 2 && factor >= 0.5) return true;
    return window.confirm(
      zin + ' van \u20ac ' + o.toFixed(2) + ' naar \u20ac ' + n.toFixed(2) + '.\n\n'
      + 'Dat is ' + (factor > 2 ? 'meer dan een verdubbeling' : 'minder dan de helft')
      + '. Controleer of de komma op de juiste plaats staat.\n\nToch opslaan?');
  };

  const slaBezorgingOp = () => {
    const kosten = parseFloat(formData.default_delivery_fee) || 0;
    const minimum = parseFloat(formData.default_min_order_amount) || 0;
    if (!bevestigBedrag('De bezorgkosten gaan', bewaard.current.default_delivery_fee, kosten)) return;
    if (!bevestigBedrag('Het minimum bestelbedrag gaat', bewaard.current.default_min_order_amount, minimum)) return;
    slaSectieOp('bezorging', {
      default_delivery_fee: kosten,
      default_min_order_amount: minimum,
      estimated_delivery_time: parseInt(formData.estimated_delivery_time) || 30,
      estimated_pickup_time: parseInt(formData.estimated_pickup_time) || 15,
    }, 'Bezorginstellingen');
  };

  const sectieKnop = (sectie, onOpslaan, label) => (
    <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
      {vuil[sectie] && (
        <span className="text-sm text-amber-600" data-testid={`onopgeslagen-${sectie}`}>
          Nog niet opgeslagen
        </span>
      )}
      <Button
        type="button"
        onClick={onOpslaan}
        disabled={!vuil[sectie] || sectieBezig === sectie}
        className="btn-primary"
        data-testid={`save-${sectie}-btn`}
      >
        <Save className="w-4 h-4 mr-2" />
        {sectieBezig === sectie ? 'Opslaan...' : label}
      </Button>
    </div>
  );

  const [copied, setCopied] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showDomainDialog, setShowDomainDialog] = useState(false);
  const [showStripeDialog, setShowStripeDialog] = useState(false);
  const [stripeConfig, setStripeConfig] = useState({
    stripe_public_key: '',
    stripe_secret_key: '',
  });
  const [savingStripe, setSavingStripe] = useState(false);
  // Stripe Connect state
  const [stripeConnectStatus, setStripeConnectStatus] = useState(null);
  const [loadingStripeConnect, setLoadingStripeConnect] = useState(false);
  const [stripeConnectTab, setStripeConnectTab] = useState('connect'); // 'connect' or 'manual'

  // Priority: verified custom domain → platform /shop/<slug>
  const webshopUrl = (restaurant?.custom_domain && restaurant?.custom_domain_verified)
    ? `https://${restaurant.custom_domain.trim().toLowerCase()}`
    : (restaurant?.slug ? `${window.location.origin}/shop/${restaurant.slug}` : '');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await authAPI.getProfile();
        const data = response.data;
        // Uitgangspunt voor "is deze sectie gewijzigd" en voor de controle op
        // bedragen die te ver verspringen.
        bewaard.current = { ...data };
        setFormData({
          name: data.name || '',
          phone: data.phone || '',
          address: data.address || '',
          postal_code: data.postal_code || '',
          city: data.city || '',
          cuisine_type: data.cuisine_type || '',
          description: data.description || '',
          logo_url: data.logo_url || '',
          cover_image_url: data.cover_image_url || '',
          receipt_logo_url: data.receipt_logo_url || '',
          receipt_font_size: data.receipt_font_size || 'normal',
          vat_number: data.vat_number || '',
          delivery_enabled: data.delivery_enabled ?? true,
          pickup_enabled: data.pickup_enabled ?? true,
          dine_in_enabled: data.dine_in_enabled ?? true,
          reservations_enabled: data.reservations_enabled ?? true,
          default_delivery_fee: data.default_delivery_fee?.toString() || '2.99',
          default_min_order_amount: data.default_min_order_amount?.toString() || '15.00',
          estimated_delivery_time: data.estimated_delivery_time?.toString() || '30',
          estimated_pickup_time: data.estimated_pickup_time?.toString() || '15',
          cash_enabled: data.cash_enabled ?? true,
          // Stond hier niet, terwijl de schakelaar het veld wel uitleest: die
          // toonde dus altijd "uit". Standaard false, want geen enkel
          // restaurant heeft het veld -- true zou stilzwijgend een
          // betaalmethode aanzetten die nu overal uit staat.
          card_enabled: data.card_enabled ?? false,
          online_payment_enabled: data.online_payment_enabled ?? true,
          currency: data.currency || 'EUR',
          default_language: data.default_language || 'nl',
          custom_domain: data.custom_domain || '',
          custom_domain_verified: data.custom_domain_verified ?? false,
          custom_domain_active: data.custom_domain_active ?? false,
          opening_hours: {
            monday: data.opening_hours?.monday || { open: '09:00', close: '22:00', closed: false },
            tuesday: data.opening_hours?.tuesday || { open: '09:00', close: '22:00', closed: false },
            wednesday: data.opening_hours?.wednesday || { open: '09:00', close: '22:00', closed: false },
            thursday: data.opening_hours?.thursday || { open: '09:00', close: '22:00', closed: false },
            friday: data.opening_hours?.friday || { open: '09:00', close: '23:00', closed: false },
            saturday: data.opening_hours?.saturday || { open: '10:00', close: '23:00', closed: false },
            sunday: data.opening_hours?.sunday || { open: '10:00', close: '22:00', closed: false },
          },
          scheduled_orders_enabled: data.scheduled_orders_enabled ?? true,
          scheduled_orders_lead_time_minutes: data.scheduled_orders_lead_time_minutes ?? 30,
          scheduled_orders_slot_interval_minutes: data.scheduled_orders_slot_interval_minutes ?? 30,
          scheduled_orders_max_days_ahead: data.scheduled_orders_max_days_ahead ?? 1,
          scheduled_orders_max_per_slot: data.scheduled_orders_max_per_slot ?? 5,
          scheduled_orders_prep_warning_minutes: data.scheduled_orders_prep_warning_minutes ?? 10,
        });
      } catch (error) {
        toast.error('Kon instellingen niet laden');
      } finally {
        setFetching(false);
      }
    };
    fetchProfile();
  }, []);

  // Fetch Stripe Connect status when dialog opens
  useEffect(() => {
    if (showStripeDialog) {
      fetchStripeConnectStatus();
    }
  }, [showStripeDialog]);

  const fetchStripeConnectStatus = async () => {
    try {
      const response = await restaurantAPI.getStripeConnectStatus();
      setStripeConnectStatus(response.data);
    } catch (error) {
      console.error('Could not fetch Stripe Connect status:', error);
    }
  };

  const handleStripeConnectStart = async () => {
    setLoadingStripeConnect(true);
    try {
      // First create the account if it doesn't exist
      let accountId = stripeConnectStatus?.account_id;
      
      if (!stripeConnectStatus?.connected) {
        const createResponse = await restaurantAPI.createStripeConnectAccount();
        accountId = createResponse.data.account_id;
      }
      
      // Create onboarding link
      const linkResponse = await restaurantAPI.createStripeConnectLink({
        return_url: `${window.location.origin}/dashboard/settings?stripe=success`,
        refresh_url: `${window.location.origin}/dashboard/settings?stripe=refresh`
      });
      
      // Redirect to Stripe
      window.location.href = linkResponse.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon Stripe Connect niet starten');
      setLoadingStripeConnect(false);
    }
  };

  const handleStripeConnectDashboard = async () => {
    setLoadingStripeConnect(true);
    try {
      const response = await restaurantAPI.createStripeConnectDashboardLink();
      window.open(response.data.url, '_blank');
    } catch (error) {
      toast.error('Kon Stripe Dashboard niet openen');
    } finally {
      setLoadingStripeConnect(false);
    }
  };

  const handleStripeConnectDisconnect = async () => {
    if (!window.confirm('Weet je zeker dat je Stripe wilt ontkoppelen? Je kunt geen online betalingen meer ontvangen.')) {
      return;
    }
    setLoadingStripeConnect(true);
    try {
      await restaurantAPI.disconnectStripeConnect();
      setStripeConnectStatus(null);
      toast.success('Stripe ontkoppeld');
      // Refresh restaurant data
      const response = await authAPI.getProfile();
      updateRestaurant(response.data);
    } catch (error) {
      toast.error('Kon Stripe niet ontkoppelen');
    } finally {
      setLoadingStripeConnect(false);
    }
  };


  const copyWebshopUrl = () => {
    navigator.clipboard.writeText(webshopUrl);
    setCopied(true);
    toast.success('Link gekopieerd!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-gray-900">Instellingen</h1>
        <p className="text-gray-500">
          Wijzigingen worden vanzelf opgeslagen. Bedragen, openingstijden en
          betaalmethodes hebben een eigen knop.
        </p>
      </div>

      {/* Vast rechtsonder in beeld. Deze pagina is lang: een statusregel naast
          de titel zie je niet meer zodra je bij Basisinformatie bent, en dan
          heb je geen enkel bewijs dat je wijziging is aangekomen. */}
      {autosave.status !== 'rust' && (
        <div className="fixed bottom-4 right-4 z-40 bg-white border border-gray-200 rounded-full shadow-lg px-4 py-2">
          <AutosaveStatus
            status={autosave.status}
            opgeslagenOp={autosave.opgeslagenOp}
            foutmelding={autosave.foutmelding}
            onOpnieuw={autosave.probeerOpnieuw}
          />
        </div>
      )}

      {/* Webshop Link Card - PROMINENT */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-lg mb-1 flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Jouw Webshop
            </h3>
            <p className="text-white/80 text-sm mb-4">Deel deze link met je klanten om bestellingen te ontvangen</p>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 flex items-center gap-3">
              <LinkIcon className="w-5 h-5 text-white/80" />
              <span className="flex-1 font-mono text-sm truncate">{webshopUrl}</span>
              <button
                onClick={copyWebshopUrl}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              className="bg-white/20 text-white border-0 hover:bg-white/30"
              onClick={() => setShowQRDialog(true)}
            >
              <QrCode className="w-4 h-4 mr-1" />
              QR Code
            </Button>
            <Link to={`/shop/${restaurant?.slug}`} target="_blank">
              <Button variant="secondary" size="sm" className="bg-white text-emerald-600 hover:bg-gray-100">
                <ExternalLink className="w-4 h-4 mr-1" />
                Bekijk Webshop
              </Button>
            </Link>
          </div>
        </div>

        {/* Custom Domain Teaser */}
        <div className="mt-4 pt-4 border-t border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-yellow-300" />
              <span className="text-sm">Eigen domein koppelen?</span>
              {formData.custom_domain && (
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                  {formData.custom_domain}
                </span>
              )}
            </div>
            <button
              onClick={() => setShowDomainDialog(true)}
              className="text-sm underline hover:no-underline"
            >
              Configureren
            </button>
          </div>
        </div>
      </div>

      {/* Domain Management Card - More Prominent */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Globe className="w-6 h-6 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">Eigen Domein</h3>
            {formData.custom_domain ? (
              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-purple-700 bg-white px-3 py-1 rounded-lg border border-purple-200">
                    {formData.custom_domain}
                  </span>
                  {formData.custom_domain_active ? (
                    <span className="flex items-center gap-1 text-emerald-600 text-sm" data-testid="domain-status-active">
                      <CheckCircle className="w-4 h-4" />
                      Actief
                    </span>
                  ) : formData.custom_domain_verified ? (
                    <span className="flex items-center gap-1 text-amber-600 text-sm" data-testid="domain-status-pending-activation">
                      <Clock className="w-4 h-4" />
                      Wacht op activatie (SSL)
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-amber-600 text-sm" data-testid="domain-status-pending-dns">
                      <Clock className="w-4 h-4" />
                      DNS controleren
                    </span>
                  )}
                </div>
                {formData.custom_domain_active && (
                  <a
                    href={`https://${formData.custom_domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-purple-600 underline mt-2 inline-block"
                    data-testid="domain-live-link"
                  >
                    Open je webshop op https://{formData.custom_domain}
                  </a>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={() => setShowDomainDialog(true)}
                >
                  Domein Beheren
                </Button>
              </div>
            ) : (
              <div className="mt-2">
                <p className="text-gray-600 text-sm mb-3">
                  Koppel je eigen domein zodat klanten kunnen bestellen via bijv. <em>bestellen.jouwrestaurant.be</em>
                </p>
                <Button 
                  onClick={() => setShowDomainDialog(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Domein Toevoegen
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Install PWA Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <Smartphone className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-blue-900">Installeer als Android App</h4>
            <p className="text-sm text-blue-700">
              Open <strong>/dashboard/live-orders</strong> op je Android telefoon en kies "Toevoegen aan startscherm" voor snelle toegang tot live bestellingen
            </p>
          </div>
        </div>
      </div>

      {/* Geen <form> meer. De meeste velden slaan zichzelf op, en de secties
          die dat bewust niet doen hebben elk hun eigen knop. Een verzamelknop
          onderaan zou nu suggereren dat de rest daarop wacht. */}
      <div className="space-y-6">
        {/* Services - IMPORTANT */}
        <div className="card-dashboard">
          <h3 className="font-heading font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#1A4D2E]" />
            Diensten
          </h3>
          <p className="text-gray-500 text-sm mb-4">Schakel in/uit welke diensten je aanbiedt</p>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className={`p-4 rounded-xl border-2 transition-all ${formData.delivery_enabled ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Truck className={`w-5 h-5 ${formData.delivery_enabled ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium text-gray-900">Bezorging</p>
                    <p className="text-xs text-gray-500">Klanten kunnen laten bezorgen</p>
                  </div>
                </div>
                <Switch
                  checked={formData.delivery_enabled}
                  onCheckedChange={(checked) => zetDienst('delivery_enabled', checked)}
                  data-testid="settings-delivery-switch"
                />
              </div>
            </div>

            <div className={`p-4 rounded-xl border-2 transition-all ${formData.pickup_enabled ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ShoppingBag className={`w-5 h-5 ${formData.pickup_enabled ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium text-gray-900">Afhalen</p>
                    <p className="text-xs text-gray-500">Klanten kunnen afhalen</p>
                  </div>
                </div>
                <Switch
                  checked={formData.pickup_enabled}
                  onCheckedChange={(checked) => zetDienst('pickup_enabled', checked)}
                  data-testid="settings-pickup-switch"
                />
              </div>
            </div>

            <div className={`p-4 rounded-xl border-2 transition-all ${formData.dine_in_enabled ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Utensils className={`w-5 h-5 ${formData.dine_in_enabled ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium text-gray-900">Ter Plaatse</p>
                    <p className="text-xs text-gray-500">Klanten kunnen ter plaatse eten</p>
                  </div>
                </div>
                <Switch
                  checked={formData.dine_in_enabled}
                  onCheckedChange={(checked) => zetDienst('dine_in_enabled', checked)}
                  data-testid="settings-dinein-switch"
                />
              </div>
            </div>

            <div className={`p-4 rounded-xl border-2 transition-all ${formData.reservations_enabled ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar className={`w-5 h-5 ${formData.reservations_enabled ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium text-gray-900">Reserveringen</p>
                    <p className="text-xs text-gray-500">Tafels kunnen gereserveerd worden</p>
                  </div>
                </div>
                <Switch
                  checked={formData.reservations_enabled}
                  onCheckedChange={(checked) => zetDienst('reservations_enabled', checked)}
                  data-testid="settings-reservations-switch"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Opening Hours */}
        <div className="card-dashboard" data-testid="opening-hours-section">
          <h3 className="font-heading font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#1A4D2E]" />
            Openingstijden
          </h3>
          <p className="text-gray-500 text-sm mb-6">Stel per dag in wanneer je geopend bent. Klanten zien dit in de webshop.</p>

          <div className="space-y-2">
            {[
              { key: 'monday', label: 'Maandag' },
              { key: 'tuesday', label: 'Dinsdag' },
              { key: 'wednesday', label: 'Woensdag' },
              { key: 'thursday', label: 'Donderdag' },
              { key: 'friday', label: 'Vrijdag' },
              { key: 'saturday', label: 'Zaterdag' },
              { key: 'sunday', label: 'Zondag' },
            ].map(({ key, label }) => {
              const day = formData.opening_hours[key] || { open: '09:00', close: '22:00', closed: false };
              return (
                <div key={key} className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 items-center py-2 border-b border-gray-100 last:border-0" data-testid={`opening-hours-${key}`}>
                  <div className="flex items-center justify-between sm:justify-start gap-3">
                    <span className="font-medium text-gray-900">{label}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Switch
                      checked={!day.closed}
                      onCheckedChange={(open) => zetUren(key, { closed: !open })}
                      data-testid={`opening-hours-${key}-toggle`}
                    />
                    {day.closed ? (
                      <span className="text-sm text-gray-500 italic">Gesloten</span>
                    ) : (
                      <>
                        <Input
                          type="time"
                          value={day.open}
                          onChange={(e) => zetUren(key, { open: e.target.value })}
                          className="w-28"
                          data-testid={`opening-hours-${key}-open`}
                        />
                        <span className="text-gray-500">tot</span>
                        <Input
                          type="time"
                          value={day.close}
                          onChange={(e) => zetUren(key, { close: e.target.value })}
                          className="w-28"
                          data-testid={`opening-hours-${key}-close`}
                        />
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 p-3 bg-blue-50 rounded-lg flex gap-2">
            <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-blue-800 text-xs">
              Tip: Zet een dag op "Gesloten" door de schakelaar uit te zetten. Openingstijden worden getoond op je webshop en bestellingen worden geweigerd buiten deze uren.
            </p>
          </div>

          {sectieKnop('uren',
            () => slaSectieOp('uren', { opening_hours: formData.opening_hours }, 'Openingstijden'),
            'Openingstijden opslaan')}
        </div>

        {/* Scheduled Orders (Pre-orders) */}
        <div className="card-dashboard" data-testid="scheduled-orders-section">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="font-heading font-semibold text-gray-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#1A4D2E]" />
                Bestellingen op afspraak
              </h3>
              <p className="text-gray-500 text-sm mt-1">Laat klanten een tijdstip kiezen (nu of vooruit plannen) voor pickup of levering.</p>
            </div>
            <Switch
              checked={formData.scheduled_orders_enabled}
              onCheckedChange={(v) => zet({ scheduled_orders_enabled: v })}
              data-testid="scheduled-orders-toggle"
            />
          </div>

          {formData.scheduled_orders_enabled && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Buffer na openingstijd (min)</Label>
                <Input
                  type="number"
                  min="0"
                  max="240"
                  value={formData.scheduled_orders_lead_time_minutes}
                  onChange={(e) => zetGetal('scheduled_orders_lead_time_minutes', e.target.value, 30)}
                  data-testid="scheduled-lead-time-input"
                />
                <p className="text-xs text-gray-500">Eerste tijdslot verschijnt X minuten na opening (bv. open 16:00 + 30 min = eerste slot 16:30).</p>
              </div>

              <div className="space-y-2">
                <Label>Interval tussen tijdsloten (min)</Label>
                <Select
                  value={formData.scheduled_orders_slot_interval_minutes.toString()}
                  onValueChange={(v) => zet({ scheduled_orders_slot_interval_minutes: parseInt(v) })}
                >
                  <SelectTrigger data-testid="scheduled-interval-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">Elke 15 min</SelectItem>
                    <SelectItem value="30">Elke 30 min</SelectItem>
                    <SelectItem value="60">Elk uur</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">Hoe fijnmazig de tijdslot-keuze is voor de klant.</p>
              </div>

              <div className="space-y-2">
                <Label>Max dagen vooruit</Label>
                <Select
                  value={formData.scheduled_orders_max_days_ahead.toString()}
                  onValueChange={(v) => zet({ scheduled_orders_max_days_ahead: parseInt(v) })}
                >
                  <SelectTrigger data-testid="scheduled-days-ahead-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Alleen vandaag</SelectItem>
                    <SelectItem value="1">Vandaag + morgen</SelectItem>
                    <SelectItem value="2">Tot 2 dagen vooruit</SelectItem>
                    <SelectItem value="3">Tot 3 dagen vooruit</SelectItem>
                    <SelectItem value="7">Tot 7 dagen vooruit</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Max bestellingen per tijdslot</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.scheduled_orders_max_per_slot}
                  onChange={(e) => zetGetal('scheduled_orders_max_per_slot', e.target.value, 5)}
                  data-testid="scheduled-max-per-slot-input"
                />
                <p className="text-xs text-gray-500">Voorkomt overbelasting: max X bestellingen op hetzelfde tijdstip.</p>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Voorbereidings­waarschuwing (min vóór tijdstip)</Label>
                <Input
                  type="number"
                  min="0"
                  max="120"
                  value={formData.scheduled_orders_prep_warning_minutes}
                  onChange={(e) => zetGetal('scheduled_orders_prep_warning_minutes', e.target.value, 10)}
                  data-testid="scheduled-prep-warning-input"
                />
                <p className="text-xs text-gray-500">Hoeveel minuten vooraf de bestelling gemarkeerd wordt als "start bereiden" (blinkend + geluid) in het live dashboard.</p>
              </div>
            </div>
          )}

          <div className="mt-4 p-3 bg-blue-50 rounded-lg flex gap-2">
            <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-blue-800 text-xs">
              Klanten die buiten je openingsuren op de webshop komen, worden automatisch gedwongen om een tijdstip te kiezen binnen je volgende openingsdag.
            </p>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="card-dashboard">
          <h3 className="font-heading font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#1A4D2E]" />
            Betaalmethodes
          </h3>
          <p className="text-gray-500 text-sm mb-4">Kies welke betaalmethodes je accepteert</p>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className={`p-4 rounded-xl border-2 transition-all ${formData.cash_enabled ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Banknote className={`w-5 h-5 ${formData.cash_enabled ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium text-gray-900">Contant</p>
                    <p className="text-xs text-gray-500">Cash betaling</p>
                  </div>
                </div>
                <Switch
                  checked={formData.cash_enabled}
                  onCheckedChange={(checked) => zetHandmatig('betaling', { cash_enabled: checked })}
                  data-testid="settings-cash-switch"
                />
              </div>
            </div>

            <div className={`p-4 rounded-xl border-2 transition-all ${formData.card_enabled ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CreditCard className={`w-5 h-5 ${formData.card_enabled ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium text-gray-900">Pinnen/Kaart</p>
                    <p className="text-xs text-gray-500">Bij bezorging/afhalen</p>
                  </div>
                </div>
                <Switch
                  checked={formData.card_enabled}
                  onCheckedChange={(checked) => zetHandmatig('betaling', { card_enabled: checked })}
                  data-testid="settings-card-switch"
                />
              </div>
            </div>

            <div className={`p-4 rounded-xl border-2 transition-all ${formData.online_payment_enabled ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className={`w-5 h-5 ${formData.online_payment_enabled ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium text-gray-900">Online</p>
                    <p className="text-xs text-gray-500">iDEAL, Bancontact</p>
                  </div>
                </div>
                <Switch
                  checked={formData.online_payment_enabled}
                  onCheckedChange={(checked) => zetHandmatig('betaling', { online_payment_enabled: checked })}
                  data-testid="settings-online-switch"
                />
              </div>
            </div>
          </div>

          {/* Stripe Configuration Section */}
          {formData.online_payment_enabled && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-purple-600" />
                    Stripe Integratie
                  </h4>
                  <p className="text-sm text-gray-500">
                    Koppel je Stripe account om online betalingen te ontvangen op je eigen bankrekening
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowStripeDialog(true)}
                  data-testid="configure-stripe-btn"
                >
                  {(restaurant?.stripe_connect_account_id || restaurant?.stripe_secret_key_configured) ? 'Stripe Beheren' : 'Stripe Koppelen'}
                </Button>
              </div>
              
              {restaurant?.stripe_connect_account_id ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Stripe Connect actief</span>
                  </div>
                  <p className="text-sm text-emerald-600 mt-1">
                    Online betalingen worden automatisch naar je bankrekening overgemaakt.
                  </p>
                </div>
              ) : restaurant?.stripe_secret_key_configured ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Stripe gekoppeld (handmatig)</span>
                  </div>
                  <p className="text-sm text-emerald-600 mt-1">
                    Online betalingen worden direct naar je Stripe account gestort.
                  </p>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-amber-700">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Stripe niet geconfigureerd</span>
                  </div>
                  <p className="text-sm text-amber-600 mt-1">
                    Koppel je Stripe account om online betalingen te kunnen ontvangen.
                  </p>
                </div>
              )}
            </div>
          )}

          {sectieKnop('betaling',
            () => slaSectieOp('betaling', {
              cash_enabled: formData.cash_enabled,
              card_enabled: formData.card_enabled,
              online_payment_enabled: formData.online_payment_enabled,
            }, 'Betaalmethodes'),
            'Betaalmethodes opslaan')}
        </div>

        {/* Basic Info */}
        <div className="card-dashboard">
          <h3 className="font-heading font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <Store className="w-5 h-5 text-[#1A4D2E]" />
            Basisinformatie
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Restaurantnaam</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => zet({ name: e.target.value })}
                placeholder="Jouw Restaurant"
                data-testid="settings-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cuisine">Keukentype</Label>
              <Input
                id="cuisine"
                value={formData.cuisine_type}
                onChange={(e) => zet({ cuisine_type: e.target.value })}
                placeholder="Italiaans, Belgisch, etc."
                data-testid="settings-cuisine-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Telefoonnummer</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => zet({ phone: e.target.value })}
                  className="pl-10"
                  placeholder="+32 2 123 45 67"
                  data-testid="settings-phone-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Straat en huisnummer</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => zet({ address: e.target.value })}
                  className="pl-10"
                  placeholder="Grote Markt 1"
                  data-testid="settings-address-input"
                />
              </div>
            </div>
            {/* Postcode en gemeente stonden alleen in het registratieformulier
                en in het adminpaneel, niet hier. Een restaurant van voor die
                velden kon ze dus nergens zelf invullen, terwijl de onboarding
                er wel op wacht. */}
            <div className="space-y-2">
              <Label htmlFor="postal_code">Postcode</Label>
              <Input
                id="postal_code"
                value={formData.postal_code}
                onChange={(e) => zet({ postal_code: e.target.value })}
                placeholder="2990"
                inputMode="numeric"
                maxLength={4}
                data-testid="settings-postalcode-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">Gemeente</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => zet({ city: e.target.value })}
                placeholder="Wuustwezel"
                data-testid="settings-city-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="vat">Ondernemingsnummer / BTW-nummer <span className="text-xs text-gray-500 font-normal">(verplicht voor Belgische webshops)</span></Label>
              <Input
                id="vat"
                value={formData.vat_number}
                onChange={(e) => zet({ vat_number: e.target.value })}
                placeholder="BE 0123.456.789"
                data-testid="settings-vat-input"
              />
              <p className="text-xs text-gray-500">Verschijnt op je webshop en op alle gedrukte bonnen.</p>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="description">Beschrijving</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => zet({ description: e.target.value })}
                placeholder="Vertel klanten over je restaurant..."
                rows={3}
                data-testid="settings-description-input"
              />
            </div>
          </div>
        </div>

        {/* Delivery Settings */}
        {formData.delivery_enabled && (
          <div className="card-dashboard">
            <h3 className="font-heading font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <Truck className="w-5 h-5 text-[#1A4D2E]" />
              Bezorginstellingen
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="delivery-fee">Standaard Bezorgkosten</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="delivery-fee"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.default_delivery_fee}
                    onChange={(e) => zetHandmatig('bezorging', { default_delivery_fee: e.target.value })}
                    className="pl-9"
                    placeholder="3.99"
                    data-testid="settings-delivery-fee-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="min-order">Minimum Bestelling</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="min-order"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.default_min_order_amount}
                    onChange={(e) => zetHandmatig('bezorging', { default_min_order_amount: e.target.value })}
                    className="pl-9"
                    placeholder="15.00"
                    data-testid="settings-min-order-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="delivery-time">Geschatte Bezorgtijd (min)</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="delivery-time"
                    type="number"
                    min="0"
                    value={formData.estimated_delivery_time}
                    onChange={(e) => zetHandmatig('bezorging', { estimated_delivery_time: e.target.value })}
                    className="pl-9"
                    placeholder="30"
                    data-testid="settings-delivery-time-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pickup-time">Geschatte Afhaaltijd (minuten)</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="pickup-time"
                    type="number"
                    min="0"
                    value={formData.estimated_pickup_time}
                    onChange={(e) => zetHandmatig('bezorging', { estimated_pickup_time: e.target.value })}
                    className="pl-9"
                    placeholder="15"
                    data-testid="settings-pickup-time-input"
                  />
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Voor specifieke bezorgzones met aparte tarieven, ga naar{' '}
              <Link to="/dashboard/delivery-zones" className="text-emerald-600 hover:underline">Bezorgzones</Link>
            </p>

            {sectieKnop('bezorging', slaBezorgingOp, 'Bezorginstellingen opslaan')}
          </div>
        )}

        {/* Language & Currency */}
        <div className="card-dashboard">
          <h3 className="font-heading font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#1A4D2E]" />
            Taal & Valuta
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="language">Standaardtaal</Label>
              <select
                id="language"
                value={formData.default_language}
                onChange={(e) => zet({ default_language: e.target.value })}
                className="w-full h-10 px-3 border border-gray-200 rounded-md"
                data-testid="settings-language-select"
              >
                <option value="nl">Nederlands</option>
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Valuta</Label>
              <select
                id="currency"
                value={formData.currency}
                onChange={(e) => zet({ currency: e.target.value })}
                className="w-full h-10 px-3 border border-gray-200 rounded-md"
                data-testid="settings-currency-select"
              >
                <option value="EUR">Euro (€)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Images / Branding */}
        <div className="card-dashboard">
          <h3 className="font-heading font-semibold text-gray-900 mb-6">Afbeeldingen & Branding</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <ImageUploadField
              id="logo"
              label="Logo"
              hint="Verschijnt bovenaan je webshop en op de bon (fallback)."
              value={formData.logo_url}
              onChange={(v) => zet({ logo_url: v })}
              maxKb={2048}
              testId="settings-logo"
              previewClass="h-20 w-20 object-cover rounded-xl"
            />
            <ImageUploadField
              id="cover"
              label="Cover afbeelding"
              hint="Grote banner bovenaan je webshop."
              value={formData.cover_image_url}
              onChange={(v) => zet({ cover_image_url: v })}
              maxKb={3072}
              testId="settings-cover"
              previewClass="h-24 w-full object-cover rounded-xl"
            />
            <ImageUploadField
              id="receipt-logo"
              label="Logo voor kassabon"
              hint="Zwart-wit werkt het best op thermische printers. Als leeg → standaard logo wordt gebruikt."
              value={formData.receipt_logo_url}
              onChange={(v) => zet({ receipt_logo_url: v })}
              maxKb={2048}
              testId="settings-receipt-logo"
              previewClass="h-20 w-auto max-w-full object-contain rounded bg-gray-50 p-2 border"
            />
          </div>
        </div>

        {/* Receipt Settings */}
        <div className="card-dashboard">
          <h3 className="font-heading font-semibold text-gray-900 mb-2">Kassabon instellingen</h3>
          <p className="text-gray-500 text-sm mb-6">Pas de lettergrootte aan zodat je bonnen goed leesbaar zijn in de keuken.</p>
          <div className="space-y-3">
            <Label>Lettergrootte kassabon</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { value: 'small', label: 'Klein', preview: 'text-xs' },
                { value: 'normal', label: 'Normaal', preview: 'text-sm' },
                { value: 'large', label: 'Groot', preview: 'text-base' },
                { value: 'xlarge', label: 'Extra groot', preview: 'text-lg' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => zet({ receipt_font_size: opt.value })}
                  className={`px-4 py-3 rounded-xl border-2 transition-all text-left ${
                    formData.receipt_font_size === opt.value
                      ? 'border-[#1A4D2E] bg-[#1A4D2E]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`receipt-font-${opt.value}`}
                >
                  <p className={`font-bold text-gray-900 ${opt.preview}`}>Aa</p>
                  <p className="text-xs text-gray-600 mt-1">{opt.label}</p>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500">Standaard is "Normaal". Kies "Groot" of "Extra groot" als je bonnen slecht leesbaar zijn in de drukte.</p>
          </div>
        </div>

      </div>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>QR Code voor je Webshop</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            <div className="bg-white p-4 rounded-xl border">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(webshopUrl)}`}
                alt="QR Code"
                className="w-48 h-48"
              />
            </div>
            <p className="text-sm text-gray-500 mt-4 text-center">
              Print deze QR code en plaats hem in je restaurant zodat klanten direct kunnen bestellen
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQRDialog(false)}>Sluiten</Button>
            <a 
              href={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(webshopUrl)}&format=png`}
              download="webshop-qr-code.png"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="btn-primary">Download QR Code</Button>
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Domain Dialog */}
      <Dialog open={showDomainDialog} onOpenChange={setShowDomainDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Eigen Domein Koppelen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-amber-800 text-sm">
                <strong>Pro/Enterprise functie:</strong> Klanten bezoeken je webshop via je eigen domein (bv. <em>www.jouwrestaurant.be</em>) met automatisch SSL-certificaat 🔒.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Je eigen domein</Label>
              <Input
                value={formData.custom_domain}
                onChange={(e) => setFormData({ ...formData, custom_domain: e.target.value })}
                placeholder="www.jouwrestaurant.be of bestellen.jouwrestaurant.be"
                data-testid="custom-domain-input"
              />
              {formData.custom_domain_active ? (
                <div className="flex items-center gap-2 text-sm text-emerald-600" data-testid="domain-active-notice">
                  <CheckCircle className="w-4 h-4" />
                  Domein is actief — je webshop is bereikbaar via https://{formData.custom_domain}
                </div>
              ) : formData.custom_domain_verified ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800" data-testid="domain-awaiting-activation-notice">
                  <strong>DNS geverifieerd!</strong> Bezoek https://{formData.custom_domain} in een incognito-venster. Ons systeem vraagt automatisch een SSL-certificaat aan bij Let&apos;s Encrypt (10-30 seconden). Daarna is je webshop meteen live op je eigen domein.
                </div>
              ) : null}
            </div>

            {(() => {
              const domain = (formData.custom_domain || '').trim().toLowerCase();
              const parts = domain.split('.').filter(Boolean);
              const isRootDomain = parts.length === 2 && domain.length > 0;   // bv. "ramses-ii.be"
              const isSubdomain  = parts.length >= 3;                          // bv. "www.ramses-ii.be"
              const subdomainName = isSubdomain ? parts[0] : 'www';
              const platformDomain = 'food-flows.com';

              if (isRootDomain) {
                return (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
                    <p className="text-sm font-semibold text-red-900">
                      ⚠️ Je gebruikt een hoofddomein (root) — DNS ondersteunt geen CNAME hier.
                    </p>
                    <p className="text-sm text-red-800">
                      Voeg een <strong>www.</strong> voor je domein: <code className="bg-white px-2 py-0.5 rounded">www.{domain}</code>
                    </p>
                    <p className="text-xs text-red-700">
                      Wil je het hoofddomein zelf ook laten werken? Stel bij je domeinprovider een <strong>URL-redirect</strong> in van <code>{domain}</code> naar <code>https://www.{domain}</code> (301 permanent).
                    </p>
                  </div>
                );
              }

              return (
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900 mb-2">
                      Stap 1 — Voeg deze DNS-record toe bij je domeinprovider (Vimexx, Namecheap, Cloudflare, …)
                    </p>
                    <div className="bg-white border border-gray-200 p-3 rounded-lg space-y-1 font-mono text-sm">
                      <div><span className="text-gray-500">Type:  </span>CNAME</div>
                      <div><span className="text-gray-500">Naam:  </span>{subdomainName}</div>
                      <div><span className="text-gray-500">Waarde:</span> {platformDomain}</div>
                      <div><span className="text-gray-500">TTL:   </span>3600 (of automatisch)</div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Vul in het &quot;Naam&quot;-veld enkel <code className="bg-gray-200 px-1 rounded">{subdomainName}</code> in (niet je volledige domein). Je provider voegt automatisch <code className="bg-gray-200 px-1 rounded">.{parts.slice(1).join('.') || 'jouwdomein.be'}</code> toe.
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      Stap 2 — Wacht tot DNS gepropageerd is (5–60 minuten)
                    </p>
                    <p className="text-xs text-gray-600">
                      Klik dan hieronder op <strong>DNS Verifiëren</strong>. Zodra de check groen is, bezoek je https://{domain || `${subdomainName}.jouwdomein.be`} in incognito-modus — het SSL-certificaat wordt <strong>automatisch aangevraagd</strong> bij Let&apos;s Encrypt tijdens je eerste bezoek (10-30 seconden).
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowDomainDialog(false)}>
              Annuleren
            </Button>
            <Button 
              variant="outline"
              onClick={async () => {
                if (!formData.custom_domain) {
                  toast.error('Voer eerst een domein in');
                  return;
                }
                setVerifyingDomain(true);
                try {
                  const response = await restaurantAPI.verifyDomain(formData.custom_domain);
                  if (response.data.verified) {
                    toast.success(response.data.message);
                    setFormData({
                      ...formData,
                      custom_domain_verified: true,
                      custom_domain_active: response.data.active ?? false,
                    });
                  } else {
                    toast.error(response.data.error);
                  }
                } catch (error) {
                  toast.error('Kon domein niet verifiëren');
                } finally {
                  setVerifyingDomain(false);
                }
              }}
              disabled={verifyingDomain || !formData.custom_domain}
            >
              {verifyingDomain ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Controleren...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  DNS Verifiëren
                </>
              )}
            </Button>
            <Button 
              className="btn-primary" 
              onClick={async () => {
                try {
                  await restaurantAPI.updateProfile({ custom_domain: formData.custom_domain });
                  toast.success('Domein opgeslagen!');
                  setShowDomainDialog(false);
                } catch (error) {
                  toast.error('Kon domein niet opslaan');
                }
              }}
            >
              Domein Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stripe Configuration Dialog */}
      <Dialog open={showStripeDialog} onOpenChange={setShowStripeDialog}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-purple-600" />
              Stripe Integratie
            </DialogTitle>
          </DialogHeader>
          
          {/* Tabs */}
          <div className="flex gap-2 border-b border-gray-200 -mx-6 px-6">
            <button
              type="button"
              onClick={() => setStripeConnectTab('connect')}
              className={`pb-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                stripeConnectTab === 'connect' 
                  ? 'border-purple-600 text-purple-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Stripe Connect (Aanbevolen)
            </button>
            <button
              type="button"
              onClick={() => setStripeConnectTab('manual')}
              className={`pb-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                stripeConnectTab === 'manual' 
                  ? 'border-purple-600 text-purple-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Handmatige Keys
            </button>
          </div>

          <div className="py-4">
            {stripeConnectTab === 'connect' ? (
              /* Stripe Connect Tab */
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-4">
                  <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Waarom Stripe Connect?
                  </h4>
                  <ul className="text-sm text-purple-700 space-y-1">
                    <li>• Geen API keys nodig - koppel met één klik</li>
                    <li>• Automatische uitbetalingen naar je bankrekening</li>
                    <li>• iDEAL, Bancontact, creditcards automatisch ingeschakeld</li>
                    <li>• Beheer alles vanuit je Stripe Dashboard</li>
                  </ul>
                </div>

                {stripeConnectStatus?.connected && stripeConnectStatus?.charges_enabled ? (
                  /* Connected and active */
                  <div className="space-y-4">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-emerald-700">
                          <CheckCircle className="w-5 h-5" />
                          <div>
                            <span className="font-medium">Stripe Connect Actief</span>
                            {stripeConnectStatus.business_name && (
                              <p className="text-sm text-emerald-600">{stripeConnectStatus.business_name}</p>
                            )}
                          </div>
                        </div>
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
                          {stripeConnectStatus.country}
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={handleStripeConnectDashboard}
                        disabled={loadingStripeConnect}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Stripe Dashboard
                      </Button>
                      <Button
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={handleStripeConnectDisconnect}
                        disabled={loadingStripeConnect}
                      >
                        Ontkoppelen
                      </Button>
                    </div>
                  </div>
                ) : stripeConnectStatus?.connected && !stripeConnectStatus?.charges_enabled ? (
                  /* Connected but incomplete */
                  <div className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-amber-700">
                        <AlertCircle className="w-5 h-5" />
                        <div>
                          <span className="font-medium">Onboarding niet voltooid</span>
                          <p className="text-sm text-amber-600 mt-1">
                            Vul je gegevens aan om online betalingen te kunnen ontvangen.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Button
                      className="w-full btn-primary"
                      onClick={handleStripeConnectStart}
                      disabled={loadingStripeConnect}
                    >
                      {loadingStripeConnect ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Laden...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Onboarding Voltooien
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  /* Not connected */
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <CreditCard className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-600 mb-4">
                        Koppel je Stripe account om online betalingen te ontvangen
                      </p>
                      <Button
                        className="btn-primary"
                        onClick={handleStripeConnectStart}
                        disabled={loadingStripeConnect}
                        data-testid="stripe-connect-start-btn"
                      >
                        {loadingStripeConnect ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Account aanmaken...
                          </>
                        ) : (
                          <>
                            <LinkIcon className="w-4 h-4 mr-2" />
                            Stripe Account Koppelen
                          </>
                        )}
                      </Button>
                    </div>

                    <p className="text-xs text-gray-500 text-center">
                      Je wordt doorgestuurd naar Stripe om je account te koppelen of aan te maken.
                      Dit duurt ongeveer 5 minuten.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              /* Manual Keys Tab */
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-blue-800 text-sm">
                    <strong>Handmatige configuratie</strong><br />
                    Als je al een Stripe account hebt, kun je je API keys hier invoeren. Bezoek{' '}
                    <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                      stripe.com/apikeys
                    </a>{' '}
                    om je keys te vinden.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stripe-public">Publishable Key (pk_...)</Label>
                  <Input
                    id="stripe-public"
                    value={stripeConfig.stripe_public_key}
                    onChange={(e) => setStripeConfig({ ...stripeConfig, stripe_public_key: e.target.value })}
                    placeholder="pk_live_..."
                    className="font-mono text-sm"
                    data-testid="stripe-public-key-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stripe-secret">Secret Key (sk_...)</Label>
                  <Input
                    id="stripe-secret"
                    type="password"
                    value={stripeConfig.stripe_secret_key}
                    onChange={(e) => setStripeConfig({ ...stripeConfig, stripe_secret_key: e.target.value })}
                    placeholder="sk_live_..."
                    className="font-mono text-sm"
                    data-testid="stripe-secret-key-input"
                  />
                  <p className="text-xs text-gray-500">
                    Je Secret Key wordt veilig opgeslagen en nooit getoond.
                  </p>
                </div>

                {restaurant?.stripe_secret_key_configured && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                    <div className="flex items-center gap-2 text-emerald-700 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      <span>Handmatige Stripe keys zijn geconfigureerd</span>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  {restaurant?.stripe_secret_key_configured && (
                    <Button 
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={async () => {
                        try {
                          await restaurantAPI.removeStripeConfig();
                          toast.success('Stripe configuratie verwijderd');
                          setShowStripeDialog(false);
                          const response = await authAPI.getProfile();
                          updateRestaurant(response.data);
                        } catch (error) {
                          toast.error('Kon Stripe configuratie niet verwijderen');
                        }
                      }}
                    >
                      Verwijderen
                    </Button>
                  )}
                  <Button 
                    className="btn-primary flex-1" 
                    disabled={savingStripe || !stripeConfig.stripe_public_key || !stripeConfig.stripe_secret_key}
                    onClick={async () => {
                      setSavingStripe(true);
                      try {
                        await restaurantAPI.saveStripeConfig(stripeConfig);
                        toast.success('Stripe succesvol gekoppeld!');
                        setShowStripeDialog(false);
                        setStripeConfig({ stripe_public_key: '', stripe_secret_key: '' });
                        const response = await authAPI.getProfile();
                        updateRestaurant(response.data);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Kon Stripe niet koppelen');
                      } finally {
                        setSavingStripe(false);
                      }
                    }}
                  >
                    {savingStripe ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Opslaan...
                      </>
                    ) : (
                      'Keys Opslaan'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStripeDialog(false)}>
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
